import { useForm } from "@inertiajs/react"

interface EquipmentForm {
  id?: number
  name: string
  equipment_type_id: number | null
  year: number | null
  manufacturer: string
  model: string
  serial_number: string
  purchased_at: string | null
  purchased_from_vendor_id: number | null
  purchase_price: number | null
  serviced_by_vendor_id: number | null
  notes: string
  hourly_rate: number | null
  daily_rate: number | null
  requires_odometer: boolean
  sort_name: string
}

interface Option {
  id: number
  name: string
}

interface Props {
  equipment: EquipmentForm
  equipment_types: Option[]
  vendors: Option[]
  errors?: Record<string, string[]>
  submitUrl: string
  submitMethod: "post" | "patch"
}

export default function EquipmentFormComponent({
  equipment,
  equipment_types,
  vendors,
  errors,
  submitUrl,
  submitMethod,
}: Props) {
  const { data, setData, post, patch, processing, transform } = useForm({
    name: equipment.name ?? "",
    equipment_type_id: equipment.equipment_type_id,
    year: equipment.year,
    manufacturer: equipment.manufacturer ?? "",
    model: equipment.model ?? "",
    serial_number: equipment.serial_number ?? "",
    purchased_at: equipment.purchased_at ?? "",
    purchased_from_vendor_id: equipment.purchased_from_vendor_id,
    purchase_price: equipment.purchase_price,
    serviced_by_vendor_id: equipment.serviced_by_vendor_id,
    notes: equipment.notes ?? "",
    hourly_rate: equipment.hourly_rate,
    daily_rate: equipment.daily_rate,
    requires_odometer: equipment.requires_odometer || false,
    sort_name: equipment.sort_name ?? "",
  })

  transform((data) => ({ equipment: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (submitMethod === "post") {
      post(submitUrl)
    } else {
      patch(submitUrl)
    }
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-3 py-2 focus:border-hpi-active-blue focus:outline-none"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Basic Information</h3>

          <div>
            <label className={labelClass}>Name *</label>
            <input
              type="text"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              className={inputClass}
              required
            />
            {errors?.name && (
              <p className="text-hpi-red mt-1 text-sm">{errors.name[0]}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Equipment Type</label>
            <select
              value={data.equipment_type_id ?? ""}
              onChange={(e) =>
                setData(
                  "equipment_type_id",
                  e.target.value ? parseInt(e.target.value) : null,
                )
              }
              className={inputClass}
            >
              <option value="">Select type...</option>
              {equipment_types.map((et) => (
                <option key={et.id} value={et.id}>
                  {et.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Year</label>
            <input
              type="number"
              value={data.year ?? ""}
              onChange={(e) =>
                setData(
                  "year",
                  e.target.value ? parseInt(e.target.value) : null,
                )
              }
              className={inputClass}
              min={1900}
              max={2100}
            />
          </div>

          <div>
            <label className={labelClass}>Manufacturer</label>
            <input
              type="text"
              value={data.manufacturer}
              onChange={(e) => setData("manufacturer", e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Model</label>
            <input
              type="text"
              value={data.model}
              onChange={(e) => setData("model", e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Serial Number</label>
            <input
              type="text"
              value={data.serial_number}
              onChange={(e) => setData("serial_number", e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Sort Name</label>
            <input
              type="text"
              value={data.sort_name}
              onChange={(e) => setData("sort_name", e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-medium">Purchase & Rates</h3>

          <div>
            <label className={labelClass}>Purchased From</label>
            <select
              value={data.purchased_from_vendor_id ?? ""}
              onChange={(e) =>
                setData(
                  "purchased_from_vendor_id",
                  e.target.value ? parseInt(e.target.value) : null,
                )
              }
              className={inputClass}
            >
              <option value="">Select vendor...</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Purchase Date</label>
            <input
              type="datetime-local"
              value={data.purchased_at ?? ""}
              onChange={(e) => setData("purchased_at", e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Purchase Price</label>
            <input
              type="number"
              step="0.01"
              value={data.purchase_price ?? ""}
              onChange={(e) =>
                setData(
                  "purchase_price",
                  e.target.value ? parseFloat(e.target.value) : null,
                )
              }
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Serviced By</label>
            <select
              value={data.serviced_by_vendor_id ?? ""}
              onChange={(e) =>
                setData(
                  "serviced_by_vendor_id",
                  e.target.value ? parseInt(e.target.value) : null,
                )
              }
              className={inputClass}
            >
              <option value="">Select vendor...</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Hourly Rate</label>
            <input
              type="number"
              step="0.01"
              value={data.hourly_rate ?? ""}
              onChange={(e) =>
                setData(
                  "hourly_rate",
                  e.target.value ? parseFloat(e.target.value) : null,
                )
              }
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Daily Rate</label>
            <input
              type="number"
              step="0.01"
              value={data.daily_rate ?? ""}
              onChange={(e) =>
                setData(
                  "daily_rate",
                  e.target.value ? parseFloat(e.target.value) : null,
                )
              }
              className={inputClass}
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="requires_odometer"
              checked={data.requires_odometer}
              onChange={(e) => setData("requires_odometer", e.target.checked)}
              className="border-hpi-light-gray h-4 w-4 rounded"
            />
            <label
              htmlFor="requires_odometer"
              className="text-hpi-dark-gray ml-2 text-sm"
            >
              Requires Odometer
            </label>
          </div>
        </div>
      </div>

      <div>
        <label className={labelClass}>Notes</label>
        <textarea
          value={data.notes}
          onChange={(e) => setData("notes", e.target.value)}
          rows={4}
          className={inputClass}
        />
      </div>

      <div className="flex justify-end space-x-4">
        <a
          href="/equipment"
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </a>
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {processing ? "Saving..." : "Save Equipment"}
        </button>
      </div>
    </form>
  )
}
